// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { useGenericQuery } from '@Queries/genericQueries';
import { BrandingParams, ServerStatus, LicenseOutput, MispStatusOutput } from 'thehive-sdk';
import client from '@Client';
import { StatusContext } from '@Context/StatusProvider';
import { getAvatarUrl } from '@Helpers/images';
import THSelector from '@Molecules/THSelector/THSelector';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { LicenseContent } from '@Organisms/License/LicenseContent/LicenseContent';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { classList } from '@Helpers/className';

import './AboutSection.scss';

interface AboutSectionProps {
    triggerElem?: ReactElement;
}

const AboutSection = ({ triggerElem }: AboutSectionProps): ReactElement => {
    const i = useLocalMessage();

    const { version, cortexServers, mispServers, gitDescription } = useContext(StatusContext);
    const { data, refetch: refetchBranding } = useGenericQuery<BrandingParams>({
        fetchOnStart: false,
        manual: true,
        retry: false,
        key: ['branding-props'],
        fetchCallback: useCallback(() => client.BrandingController.list(), []),
    });

    const { data: licenseDetails, refetch: refetchLicenseDetails } = useGenericQuery({
        fetchCallback: () => client.LicenseController.get<LicenseOutput>(),
        key: ['license'],
        manual: true,
        fetchOnStart: false,
    });

    const { data: mispStatus, refetch: refetchMispStatus } = useGenericQuery<MispStatusOutput>({
        fetchOnStart: false,
        manual: true,
        retry: false,
        key: ['misp-status'],
        fetchCallback: useCallback(() => client.MispController.getStatus(), []),
    });

    const { title, menuLogo, loginLogo } = data || {};

    const { fallback, license, error, notFound } = licenseDetails || {};

    return (
        <THDrawer
            title={
                <div className="about-section__header">
                    <img
                        src={menuLogo && menuLogo.trim() !== '' ? getAvatarUrl(menuLogo) : `static/images/logo.bee.svg`}
                        className="about-section__header__img"
                    />
                    <span>{title}</span>
                </div>
            }
            footer={() => (
                <span className="about-section__footer">
                    Copyright © 2023
                    <ExternalLink title="StrangeBee" url="https://www.strangebee.com/">
                        StrangeBee
                    </ExternalLink>
                </span>
            )}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                if (visible) {
                    refetchMispStatus();
                    refetchLicenseDetails();
                    refetchBranding();
                }
            }}
        >
            <div className="about-section">
                <div className="about-section__img-container">
                    <img
                        className="about-section__img"
                        src={
                            loginLogo && loginLogo.trim() !== ''
                                ? getAvatarUrl(loginLogo)
                                : 'static/login-logo-thehive.svg'
                        }
                    />
                </div>

                <div>
                    <TitleSection>{i('general.versions')}</TitleSection>
                    <div className="about-section__grid">
                        <span className="about-section__grid__label">TheHive</span>
                        <span className="about-section__grid__value">{version}</span>
                    </div>
                    {version.includes('SNAPSHOT') ? (
                        <div className="about-section__grid">
                            <span className="about-section__grid__label">Commit</span>
                            <span className="about-section__grid__value">{gitDescription}</span>
                        </div>
                    ) : null}
                </div>

                <div>
                    <TitleSection>Cortex</TitleSection>
                    <THSelector<ServerStatus> dataSource={cortexServers} disabled refKey="name">
                        {({ name, status, version, url, alerts = [] }) => (
                            <div
                                className={classList('about-section__list__row', {
                                    'about-section__list__row__cortex': alerts.length == 0,
                                })}
                            >
                                <span>
                                    {name} {version && `- ${version}`}
                                </span>
                                <span className="about-section__list__url">{url}</span>
                                {alerts.map((alert, index) => (
                                    <span key={alert + index} className="about-section__list__update">
                                        {alert}
                                    </span>
                                ))}
                                <span className="about-section__list__status">
                                    <ColorLabel label={status} color={status === 'OK' ? 'green' : 'red'} />
                                </span>
                            </div>
                        )}
                    </THSelector>
                </div>

                <div>
                    <TitleSection>MISP</TitleSection>
                    <THSelector<ServerStatus> dataSource={mispServers} disabled refKey="name">
                        {({ name, version, url, status }) => (
                            <div className="about-section__list__row">
                                <span>
                                    {name} {version && `- ${version}`}
                                </span>
                                <span className="about-section__list__url">{url}</span>
                                <span className="about-section__list__sync-date">
                                    {mispStatus?.status[name]?.lastSync && (
                                        <>
                                            {`${i('common.last-sync-date')}:`}
                                            <FormatDate date={mispStatus?.status[name]?.lastSync} />
                                        </>
                                    )}
                                </span>
                                <span className="about-section__list__status">
                                    <ColorLabel label={status} color={status === 'OK' ? 'green' : 'red'} />
                                </span>
                            </div>
                        )}
                    </THSelector>
                </div>

                {(fallback || license) && (
                    <div>
                        <TitleSection>{i('general.license')}</TitleSection>
                        <LicenseContent
                            about
                            className="about-section__license"
                            license={fallback || license}
                            plan={license ? license.plan : i(error ? 'general.error' : 'general.not-found')}
                            notFound={!license && notFound}
                        />
                    </div>
                )}
            </div>
        </THDrawer>
    );
};

export default AboutSection;
